import { getAllApplicationContainers } from "services/requests/v1/container";
import { getUserPermissionsByUid } from "services/requests/v1/user";

import { SetStateType } from "types/generic";

import { FormFields } from "components/adhoc/usermodalcontent";

import { emailRegExp, nameRegExp, passwordRegExp } from "utils/regexps";
import { makeDebounceFunction } from "utils/functions";
import {
    ALLOW_DEFAULT_LANGUAGE,
    getEnvVariable,
    REACT_APP_EDIT_USERS_ENABLED,
} from "utils/environment";

type MakeUserModalContentFunctionsParams = {
    availableGrids: any;
    formData: Record<FormFields, string>;
    formErrors: Record<FormFields, boolean>;
    userStatus: boolean;
    isOpen: boolean;
    editUser: boolean;
    setMetadata: SetStateType;
    setCallbackTrigger: (callbackTrigger: boolean) => void;
    checkIfTokenIsValid: () => void;
    setFormData: SetStateType<Record<FormFields, string>>;
    setDataHasBeenEdited: SetStateType<boolean>;
    setFormErrors: SetStateType<Record<FormFields, boolean>>;
    setIsOpen: (isOpen: boolean) => void;
    setSaveDisable: SetStateType<"enabled" | "disabled">;
    setPhoneFormatError: SetStateType;
    setUserStatus: SetStateType<boolean>;
};

const validateFormDebounce = makeDebounceFunction();

export const makeUserModalContentFunctions = ({
    availableGrids,
    formData,
    userStatus,
    isOpen,
    formErrors,
    editUser,
    setCallbackTrigger,
    setMetadata,
    checkIfTokenIsValid,
    setFormData,
    setDataHasBeenEdited,
    setFormErrors,
    setIsOpen,
    setSaveDisable,
    setPhoneFormatError,
    setUserStatus,
}: MakeUserModalContentFunctionsParams) => {
    const retrieveDataFromGridUsingApi = (api: any) => {
        let permissionData: any[] = [];
        api?.forEachNode((node: any) =>
            node.data && node.isModified
                ? permissionData.push(node.data)
                : void 0
        );
        return permissionData;
    };

    const retrievePermissionDataFromGrids = () => {
        const grids = Object.values(availableGrids);
        let permissions: any = [];
        grids.forEach((grid: any) => {
            permissions = [
                ...permissions,
                retrieveDataFromGridUsingApi(grid.api),
            ];
        });
        return permissions;
    };

    const preparePermissionsForUpdateRequest = () => {
        const permissionsFromGrids = retrievePermissionDataFromGrids();
        let allowedPermissions: string[] = [];
        let disallowedPermissions: string[] = [];
        permissionsFromGrids.forEach((permissionNodes: any) => {
            permissionNodes
                .filter((node: any) => node.checked)
                .map((node: any) => allowedPermissions.push(node.title));
            permissionNodes
                .filter(
                    (node: any) => node.checked !== undefined && !node.checked
                )
                .map((node: any) => disallowedPermissions.push(node.title));
        });

        return [allowedPermissions, disallowedPermissions];
    };

    const sendRequests = async () => {
        setMetadata(retrieveUserDetailsFromInputs());
        setCallbackTrigger(true);
    };

    const retrieveUserDetailsFromInputs = () => {
        const [allowedPermissions, disallowedPermissions] =
            preparePermissionsForUpdateRequest();
        return {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            username: formData.username,
            password: formData.password,
            enabled: userStatus,
            locale: formData.locale,
            telephone: formData.telephone,
            allowedPermissions,
            disallowedPermissions,
        };
    };

    const retrieveGridsFromApplicationContainers = async (userId?: string) => {
        let gridGroups: any = {};
        let containers = null;
        if (userId && userId !== undefined) {
            checkIfTokenIsValid();
            containers = (await getUserPermissionsByUid(userId)).data;
            containers.forEach((container: any) => {
                console.log(container);
                if (!(container.group in gridGroups)) {
                    gridGroups[container.group] = {
                        columnDefinitions: [],
                        api: null,
                        rowData: [],
                    };
                }

                gridGroups[container.group]["rowData"].push({
                    group: container.subGroup,
                    title: container.name,
                    checked: container.checked ? container.checked : false,
                    client: container.client || "",
                });
                gridGroups[container.group]["columnDefinitions"] =
                    generateDefsWithHeader(container.group);
            });
        } else {
            containers = (await getAllApplicationContainers(false)).data;
            containers.forEach((container: any) => {
                if (!(container.group in gridGroups)) {
                    gridGroups[container.group] = {
                        columnDefinitions: [],
                        api: null,
                        rowData: [],
                    };
                }
                container.items.forEach((item: any) => {
                    gridGroups[container.group]["rowData"].push({
                        group: container.title,
                        title: item.title,
                        checked: false,
                        client: container.client || "",
                    });
                    gridGroups[container.group]["columnDefinitions"] =
                        generateDefsWithHeader(container.group);
                });
            });
        }
        return gridGroups;
    };

    const generateDefsWithHeader = (title: string) => {
        return [
            {
                headerName: title,
                field: "title",
                flex: 1,
                cellRenderer: "groupCheckboxRenderer",
                checkboxSelection: true,
                cellStyle: {
                    display: "flex",
                    alignItems: "center",
                },
            },
            { field: "group", rowGroup: true, hide: true },
        ];
    };

    const validateFormData = (value: string, field: FormFields) => {
        const regexpMap: Record<string, RegExp> = {
            password: passwordRegExp,
            repeatPassword: passwordRegExp,
            username: /\w/,
            email: emailRegExp,
        };

        if (field === "telephone") return true;
        if (field === "repeatPassword") return formData.password === value;
        if (field === "locale" && getEnvVariable(ALLOW_DEFAULT_LANGUAGE) === "false"){
            return true;
        }

        const fieldregexp = regexpMap[field] || nameRegExp;
        return fieldregexp.test(value);
    };

    const handleFormDataChange = (
        value: string,
        field: FormFields,
        onlyletters = false,
        ignoreBounce = false
    ) => {
        if (onlyletters) {
            const numbersRegexp = /\d/;
            if (numbersRegexp.test(value)) {
                return;
            }
        }

        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        setDataHasBeenEdited(true);

        const fieldsToDebounceValidate = [
            "email",
            "password",
            "repeatPassword",
        ];

        if (fieldsToDebounceValidate.includes(field) && ignoreBounce) {
            validateFormDebounce(() => {
                const isValid = validateFormData(value, field);

                setFormErrors((prev) => ({
                    ...prev,
                    [field]: !isValid,
                }));
            }, 100);
        } else {
            const isValid = validateFormData(value, field);

            setFormErrors((prev) => ({
                ...prev,
                [field]: !isValid,
            }));
        }
    };

    const handleSaveClick = () => {
        sendRequests();
        setIsOpen(!isOpen);
    };

    const getEditUserIsEnabled = () => {
        return getEnvVariable(REACT_APP_EDIT_USERS_ENABLED) === "true";
    };

    const verifyIfHasSomeError = () => {
        return Object.values(formErrors).some((error) => error);
    };

    const verifyIfAnyRequiredFieldIsEmpty = () => {
        return Object.entries(formData).some(([key, value]) => {
            if (key === "telephone") {
                return false;
            }
            if (key === "locale" && getEnvVariable(ALLOW_DEFAULT_LANGUAGE) === "false"){
                return false;
            }
            if (
                editUser === true &&
                (key === "password" || key === "repeatPassword")
            )
                return false;

            return value?.trim() === "";
        });
    };

    const validateFieldsAndEnableSaveButton = () => {
        const hasEmptyFields = verifyIfAnyRequiredFieldIsEmpty();
        const hasSomeError = verifyIfHasSomeError();

        if (hasEmptyFields || hasSomeError) return;

        setSaveDisable("enabled");
    };

    const validateTelephone = (numberIsValid: boolean, telephone: string) => {
        setPhoneFormatError(telephone === "" ? false : !numberIsValid);

        setFormErrors((prev) => ({
            ...prev,
            telephone: telephone === "" ? false : !numberIsValid,
        }));

        if (telephone === "") validateFieldsAndEnableSaveButton();
    };

    const handleUserStatusSwitchChanges = () => {
        setUserStatus(!userStatus);

        if (editUser) validateFieldsAndEnableSaveButton();
    };

    return {
        handleUserStatusSwitchChanges,
        validateTelephone,
        getEditUserIsEnabled,
        handleSaveClick,
        handleFormDataChange,
        retrieveGridsFromApplicationContainers,
        validateFormData,
        verifyIfAnyRequiredFieldIsEmpty,
        verifyIfHasSomeError,
    };
};
